import React from "react";
import { ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faUser,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons";

import useAdminProfileSection from "../../services/Admin/useAdminProfileSection";

const ProfileSection = ({ Admin }) => {
  const {
    activeTab,
    setActiveTab,

    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,

    user,

    loading,
    actionLoading,

    formDataProfile,
    formData,

    handleChange,
    handleChangeProfile,

    editClientsCredentialCall,
    editClientsDetailsCall,
  } = useAdminProfileSection({ Admin });

  const inputClass =
    "h-[42px] w-full rounded-md border border-gray-300 bg-white px-3 text-[15px] font-medium text-gray-800 outline-none transition placeholder:font-normal placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

  const labelClass = "mb-1 block text-[15px] font-semibold text-gray-700";

  const sectionDivider = "my-5 border-gray-200";

  const Field = ({
    label,
    name,
    value,
    type = "text",
    placeholder,
    onChange,
    children,
  }) => {
    return (
      <div className="mb-4">
        <label className={labelClass}>{label}</label>
        {children || (
          <input
            type={type}
            name={name}
            value={value || ""}
            onChange={onChange}
            placeholder={placeholder}
            className={inputClass}
          />
        )}
      </div>
    );
  };

  const RadioField = ({ name, value, checked, label, onChange }) => {
    return (
      <label className="mr-6 inline-flex cursor-pointer items-center gap-2 text-[15px] font-medium text-gray-700">
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
          className="h-4 w-4 accent-blue-600"
        />
        {label}
      </label>
    );
  };

  const CheckboxField = ({ label }) => {
    return (
      <label className="inline-flex cursor-pointer items-center gap-2 text-[15px] font-medium text-gray-700">
        <input type="checkbox" className="h-4 w-4 accent-blue-600" />
        {label}
      </label>
    );
  };

  return (
    <div className="min-h-screen bg-[#dfedff36] p-4">
      <ToastContainer />

      <div className="rounded-lg bg-white p-4">
        {/* Header */}
        <h2 className="flex items-center text-2xl font-bold text-gray-700">
          <FontAwesomeIcon icon={faUser} className="mr-2" />
          User Profile
        </h2>

        <h3 className="mt-2 text-xl font-semibold text-gray-600">
          {user?.client_name}
        </h3>

        <hr className="my-4 border-gray-200" />

        {/* Tabs */}
        <div className="mb-5 border-b border-gray-200">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("profile")}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-[15px] font-semibold transition ${
                activeTab === "profile"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-blue-600"
              }`}
            >
              <FontAwesomeIcon icon={faUser} />
              Profile
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("billing")}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-[15px] font-semibold transition ${
                activeTab === "billing"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-blue-600"
              }`}
            >
              <FontAwesomeIcon icon={faCreditCard} />
              Billing
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <div className="flex items-center gap-3">
              <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
              <span className="font-semibold text-gray-500">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <form onSubmit={editClientsDetailsCall}>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Field label="User Role">
                      <select className={inputClass}>
                        <option value="">Select Role</option>
                        <option value="admin">Administrator</option>
                        <option value="manager">Manager</option>
                        <option value="user">User</option>
                        <option value="guest">Guest</option>
                      </select>
                    </Field>
                  </div>

                  <div className="md:col-span-2">
                    <Field
                      label="Username"
                      name="client_name"
                      value={formDataProfile.client_name}
                      onChange={handleChangeProfile}
                      placeholder="Enter username"
                    />
                  </div>

                  <Field label="Password">
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        className={`${inputClass} pr-10`}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600"
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </Field>

                  <Field label="Confirm Password">
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        className={`${inputClass} pr-10`}
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600"
                      >
                        <FontAwesomeIcon
                          icon={showConfirmPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </Field>
                </div>

                <hr className={sectionDivider} />

                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  <Field
                    label="First Name"
                    name="first_name"
                    value={formDataProfile.first_name}
                    onChange={handleChangeProfile}
                    placeholder="Enter first name"
                  />

                  <Field
                    label="Last Name"
                    name="last_name"
                    value={formDataProfile.last_name}
                    onChange={handleChangeProfile}
                    placeholder="Enter last name"
                  />

                  <Field
                    label="Email Address"
                    name="client_email"
                    value={formDataProfile.client_email}
                    onChange={handleChangeProfile}
                    type="email"
                    placeholder="Enter email address"
                  />

                  <Field
                    label="Mobile Number"
                    name="client_mobile"
                    value={formDataProfile.client_mobile}
                    onChange={handleChangeProfile}
                    type="tel"
                    placeholder="Enter mobile number"
                  />

                  <Field
                    label="Billing Name"
                    name="billing_name"
                    value={formDataProfile.billing_name}
                    onChange={handleChangeProfile}
                    placeholder="Enter billing name"
                  />

                  <Field
                    label="Account Expiry On"
                    name="exprired_on"
                    value={formDataProfile.exprired_on}
                    onChange={handleChangeProfile}
                    type="date"
                  />

                  <Field
                    label="Activation Date"
                    name="activated_on"
                    value={formDataProfile.activated_on}
                    onChange={handleChangeProfile}
                    type="date"
                  />

                  <Field
                    label="Whitelisted At"
                    name="whitelistedAt"
                    value={formDataProfile.whitelistedAt}
                    onChange={handleChangeProfile}
                    type="number"
                  />

                  <Field
                    label="Allow IPs"
                    name="allowed_ip"
                    value={formDataProfile.allowed_ip}
                    onChange={handleChangeProfile}
                    placeholder="Enter allowed IPs"
                  />
                </div>

                <div className="mb-4">
                  <CheckboxField label="Apply Free Conversation" />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Data Encryption</label>
                  <div>
                    <RadioField
                      name="encryption"
                      value="yes"
                      label="Yes"
                      onChange={() => {}}
                    />
                    <RadioField
                      name="encryption"
                      value="no"
                      label="No"
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Actions</label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      "Add",
                      "Edit",
                      "Delete",
                      "Upload",
                      "Email",
                      "Daily Report",
                      "Old Chat Logs",
                    ].map((action) => (
                      <CheckboxField key={action} label={action} />
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Status</label>
                  <div>
                    <RadioField
                      name="status"
                      value="Y"
                      label="Active"
                      checked={formDataProfile.status === "Y"}
                      onChange={handleChangeProfile}
                    />
                    <RadioField
                      name="status"
                      value="N"
                      label="Inactive"
                      checked={formDataProfile.status !== "Y"}
                      onChange={handleChangeProfile}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {actionLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}

            {/* Billing Tab */}
            {activeTab === "billing" && (
              <form onSubmit={editClientsCredentialCall}>
                <h5 className="mb-3 text-lg font-bold text-gray-800">
                  One Time Charge
                </h5>

                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  <Field
                    label="Whatsapp Setup Charges"
                    name="whatsapp_setup_charges"
                    value={formData.whatsapp_setup_charges}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter setup charges"
                  />
                </div>

                <hr className={sectionDivider} />

                <h5 className="mb-3 text-lg font-bold text-gray-800">
                  Monthly Charge
                </h5>

                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  <Field
                    label="Server Charge"
                    name="server_charges"
                    value={formData.server_charges}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter server charge"
                  />

                  <Field
                    label="Monthly Recurring Charge"
                    name="monthly_reccuring_charges"
                    value={formData.monthly_reccuring_charges}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter recurring charge"
                  />

                  <Field
                    label="Other Charges"
                    name="other_charges"
                    value={formData.other_charges}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter other charges"
                  />

                  <Field
                    label="Number of Chats"
                    name="number_of_chats"
                    value={formData.number_of_chats}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter number of chats"
                  />

                  <Field
                    label="Live Chat Agent Charge"
                    name="live_chat_agent_charges"
                    value={formData.live_chat_agent_charges}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter agent charge"
                  />

                  <Field
                    label="Submission Percentage"
                    name="submissionPercentage"
                    value={formData.submissionPercentage}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter agent charge"
                  />
                </div>

                <hr className={sectionDivider} />

                <div className="mb-4">
                  <label className={labelClass}>Account Type</label>
                  <div>
                    <RadioField
                      name="accountType"
                      value="postpaid"
                      label="Postpaid"
                      checked={formData.account_type === "postpaid"}
                      onChange={handleChange}
                    />
                    <RadioField
                      name="accountType"
                      value="prepaid"
                      label="Prepaid"
                      checked={formData.account_type === "prepaid"}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  <Field
                    label="Receive Amount"
                    name="receive_amount"
                    value={formData.receive_amount}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter receive amount"
                  />

                  <Field
                    label="Email Alert Limit"
                    name="email_alert_limit"
                    value={formData.email_alert_limit}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter email alert limit"
                  />

                  <Field
                    label="Min Balance Limit"
                    name="min_balance_limit"
                    value={formData.min_balance_limit}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter min balance limit"
                  />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Billing On</label>
                  <div>
                    <RadioField
                      name="billingOn"
                      value="submission"
                      label="Submission Message"
                      checked={formData.billed_on === "submission"}
                      onChange={handleChange}
                    />
                    <RadioField
                      name="billingOn"
                      value="delivered"
                      label="Delivered Message"
                      checked={formData.billed_on === "delivered"}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  <Field
                    label="GST Number"
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleChange}
                    placeholder="Enter GST number"
                  />

                  <Field
                    label="Customer ID"
                    name="customer_id"
                    value={formData.customer_id}
                    onChange={handleChange}
                    type="number"
                    placeholder="Enter customer ID"
                  />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Billing Type</label>
                  <div>
                    <RadioField
                      name="billingType"
                      value="transaction"
                      label="Transaction Based"
                      checked={formData.billing_type === "transaction"}
                      onChange={handleChange}
                    />
                    <RadioField
                      name="billingType"
                      value="conversation"
                      label="Conversation Based"
                      checked={formData.billing_type === "conversation"}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <hr className={sectionDivider} />

                <h5 className="mb-3 text-lg font-bold text-gray-800">
                  PIN Charges
                </h5>

                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  {[
                    "marketing",
                    "utility",
                    "authentication",
                    "service",
                    "feedback",
                    "response",
                  ].map((type) => (
                    <Field
                      key={type}
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                      name={`${type}_pin_charges`}
                      value={formData[`${type}_pin_charges`]}
                      onChange={handleChange}
                      type="number"
                      placeholder={`Enter ${type} PIN charge`}
                    />
                  ))}
                </div>

                <Field label="Remark">
                  <textarea
                    name="remark"
                    value={formData.remark}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Enter remarks"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[15px] font-medium text-gray-800 outline-none transition placeholder:font-normal placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </Field>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-600"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {actionLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;